import type { AdminMedia, MediaRef } from '@diora/contracts';

/**
 * What the picker hands back.
 *
 * The whole reference, not just an ID — otherwise the caller has just chosen an
 * image it cannot render until the next refetch, and the preview box sits empty
 * next to a button that says the image was changed.
 */
export type PickedImage = Pick<MediaRef, 'id' | 'width' | 'height' | 'thumbUrl' | 'url'>;

/**
 * An asset as the form needs it.
 *
 * Both the upload button and the chooser hand back a whole reference rather
 * than an id, so the preview can render immediately — and both must agree on
 * which variant is the thumbnail and which is the full image.
 */
const toPicked = (media: AdminMedia): PickedImage => ({
  id: media.id,
  width: media.width,
  height: media.height,
  thumbUrl: media.urls.thumb ?? media.urls.medium ?? media.urls.original ?? '',
  url: media.urls.large ?? media.urls.original ?? '',
});
import { useQuery } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiGet } from '@/lib/api';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { Badge, Button, EmptyState, Modal, SkeletonRows } from './ui';
import { UploadButton } from './upload-button';

/**
 * Choose an image for a record.
 *
 * Surfaces each image's eligibility for *this* surface, because the minimum
 * dimensions are enforced at publish time — and finding out then, several
 * screens later, is a worse moment to learn a photo is too small.
 */
/*
 * A dimension pair is data, not prose. Inside an RTL column the bidi algorithm
 * reorders "640×480" to "480×640" — not merely mirrored, but a different
 * measurement. Every one of them is pinned to LTR.
 */
export function ImagePicker({
  value,
  onChange,
  surface,
  label,
  hint,
  disabled,
}: {
  value: MediaRef | null;
  onChange: (image: PickedImage | null) => void;
  surface: 'category' | 'item' | 'offer';
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canUpload = can('OWNER', 'EDITOR');
  const [open, setOpen] = useState(false);

  return (
    <div className="dio-field">
      <span className="dio-label">{label}</span>

      <div className="flex items-center gap-3">
        <div
          className="dio-image-fallback dio-image-fallback--square"
          style={{ inlineSize: '5rem', blockSize: '5rem', overflow: 'hidden' }}
        >
          {value ? (
            <img
              src={value.thumbUrl}
              alt=""
              width={value.width}
              height={value.height}
              style={{ inlineSize: '100%', blockSize: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageOff className="dio-icon" aria-hidden="true" />
          )}
        </div>

        <div className="flex flex-col gap-2">
          {/*
           * Uploading is the action, not a fallback inside a dialog.
           *
           * Adding a photo to a dish is the common case and having a picture of
           * it already in the library is the rare one, so the form does it
           * directly: choose a file, and it is uploaded and assigned. The
           * library stays one button away for the images that are worth
           * reusing — a branch photo on the about page, say.
           */}
          <div className="flex flex-wrap gap-2">
            {canUpload && !disabled && (
              <UploadButton variant="primary" size="sm" onUploaded={(media) => onChange(toPicked(media))} />
            )}
            <Button variant="secondary" size="sm" disabled={disabled} onClick={() => setOpen(true)}>
              {t('media.chooseExisting')}
            </Button>
            {value && (
              <Button variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
                {t('media.removeImage')}
              </Button>
            )}
          </div>
          {value ? (
            <span className="dio-meta tabular" dir="ltr">
              {value.width}×{value.height}
            </span>
          ) : (
            <span className="dio-meta">{t('media.noImage')}</span>
          )}
        </div>
      </div>

      {hint && <p className="dio-hint">{hint}</p>}

      <MediaChooser
        open={open}
        surface={surface}
        onClose={() => setOpen(false)}
        onPick={(picked) => {
          onChange(picked);
          setOpen(false);
        }}
      />
    </div>
  );
}

function MediaChooser({
  open,
  surface,
  onClose,
  onPick,
}: {
  open: boolean;
  surface: 'category' | 'item' | 'offer';
  onClose: () => void;
  onPick: (image: PickedImage) => void;
}) {
  const { t } = useTranslation();
  const params = { page: 1, pageSize: 40 };

  const { can } = useAuth();
  const canUpload = can('OWNER', 'EDITOR');

  const query = useQuery({
    queryKey: qk.media(params),
    queryFn: () => apiGet<AdminMedia[]>('/admin/media', params),
    enabled: open,
  });

  const rows = query.data?.data ?? [];

  /**
   * Pick what you just uploaded.
   *
   * Uploading from inside a chooser has exactly one purpose, and making
   * somebody hunt for the new image in the grid afterwards is busywork.
   */
  const pickUploaded = (media: AdminMedia) => onPick(toPicked(media));

  return (
    <Modal open={open} onClose={onClose} title={t('media.selectImage')}>
      {query.isLoading ? (
        <SkeletonRows rows={3} />
      ) : query.isError ? (
        /* An empty library and an API that did not answer are different things,
           and rendering the second as the first tells an editor their
           photographs are gone. */
        <EmptyState
          title={t('common.somethingBroke')}
          action={
            <Button size="sm" onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        /* The dead end this dialog used to be: nothing to choose, and no way to
           add anything without leaving for the media screen and coming back. */
        <EmptyState
          title={t('media.empty')}
          hint={t('media.dropHint')}
          action={canUpload ? <UploadButton size="sm" onUploaded={pickUploaded} /> : undefined}
        />
      ) : (
        <>
          {/* Available with a grid too, not only when the library is empty: the
              image somebody wants is most often the one they have not uploaded
              yet. */}
          {canUpload && (
            <div className="mb-3 flex justify-end">
              <UploadButton variant="secondary" size="sm" onUploaded={pickUploaded} />
            </div>
          )}
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {rows.map((media) => {
              const eligible = media.eligibleFor[surface];
              return (
                <li key={media.id}>
                  <button
                    type="button"
                    className="dio-card dio-card--flat w-full text-start"
                    onClick={() => onPick(toPicked(media))}
                    // Too small is a warning, not a block: an editor may be
                    // assigning it to a draft they do not intend to publish yet.
                    aria-describedby={eligible ? undefined : `${media.id}-warn`}
                  >
                    <img
                      src={media.urls.thumb ?? media.urls.medium ?? media.urls.original ?? ''}
                      alt={media.alt.ar || media.alt.en || media.originalName}
                      style={{ inlineSize: '100%', aspectRatio: '1', objectFit: 'cover' }}
                    />
                    <div className="p-2">
                      <p className="dio-meta tabular" dir="ltr">
                        {media.width}×{media.height}
                      </p>
                      {!eligible && (
                        <span id={`${media.id}-warn`}>
                          <Badge tone="warning">{t('media.tooSmallFor', { surfaces: t(`media.for${surface[0]!.toUpperCase()}${surface.slice(1)}` as 'media.forItem') })}</Badge>
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
}
